/** Season defensive buckets (roster/lineup API). */
export type PositionBuckets = {
  primaryPosition?: string[];
  secondaryPosition?: string[];
  tertiaryPosition?: string[];
};

/** Row shape for roster / waiver payloads with optional top-level buckets or `rwPlayer.seasonPositions`. */
export type RosterRowWithSeasonPositions = PositionBuckets & {
  rwPlayer?: {
    seasonPositions?: {
      primaryPosition?: string[] | null;
      secondaryPosition?: string[] | null;
      tertiaryPosition?: string[] | null;
    } | null;
  };
};

/**
 * Format P1/P2/P3 tiers like legacy `position_1/position_2/position_3` (commas within a tier, `/` between tiers).
 * Falls back to `rwPlayer.seasonPositions` when the top-level arrays are missing (e.g. free agents, waiver wire).
 */
export function formatPositionsList(buckets: RosterRowWithSeasonPositions): string {
  const sp = (buckets as any).rwPlayer?.seasonPositions;
  const prim = Array.isArray(buckets.primaryPosition) && buckets.primaryPosition.length > 0
    ? buckets.primaryPosition.filter(Boolean)
    : (Array.isArray(sp?.primaryPosition) ? sp.primaryPosition.filter(Boolean) : []);
  const sec = Array.isArray(buckets.secondaryPosition) && buckets.secondaryPosition.length > 0
    ? buckets.secondaryPosition.filter(Boolean)
    : (Array.isArray(sp?.secondaryPosition) ? sp.secondaryPosition.filter(Boolean) : []);
  const ter = Array.isArray(buckets.tertiaryPosition) && buckets.tertiaryPosition.length > 0
    ? buckets.tertiaryPosition.filter(Boolean)
    : (Array.isArray(sp?.tertiaryPosition) ? sp.tertiaryPosition.filter(Boolean) : []);
  const parts = [prim.join(','), sec.join(','), ter.join(',')].filter((s) => s.length > 0);
  return parts.length ? parts.join('/') : '—';
}



/**
 * All defensive position codes from primary/secondary/tertiary buckets (for filtering, e.g. multi-select POS).
 */
export function positionBucketTokens(row: RosterRowWithSeasonPositions): string[] {
  const sp = row.rwPlayer?.seasonPositions;
  const prim = row.primaryPosition ?? sp?.primaryPosition;
  const sec = row.secondaryPosition ?? sp?.secondaryPosition;
  const ter = row.tertiaryPosition ?? sp?.tertiaryPosition;
  const merge = [
    ...(Array.isArray(prim) ? prim : []),
    ...(Array.isArray(sec) ? sec : []),
    ...(Array.isArray(ter) ? ter : []),
  ];
  return merge.map((t) => String(t).trim()).filter(Boolean);
}
